import { useState, useEffect } from "react";
import { Col, InputGroup } from "react-bootstrap";
import { Highlighter } from "react-bootstrap-typeahead";
import { FormProvider, useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { toast } from "react-toastify";
import { useMutation } from "@tanstack/react-query";

import { useGetApprovalLevelCreateOptions } from "api/queries/user/useGetApprovalLevelCreateOptions";
import { useGetApprovalLevelDetails } from "api/queries/user/useGetApprovalLevelDetails";
import { useGetCurrencies } from "api/queries/common/useGetCurrencies";
import Button from "components/atoms/Button/Button";
import Dropdown from "components/atoms/Dropdown/Dropdown";
import ExpandableSearch from "components/atoms/ExpandableSearch/ExpandableSearch";
import Input from "components/atoms/Input/Input";
import Alert from "components/molecules/Alert/Alert";
import { deleteApprovalLevel, updateApprovalLevel, createApprovalLevel } from "api";
import Dialog from "components/organisms/Dialog/Dialog";
import SearchSelect from "pages/shipper/RequestForm/components/Common/SearchSelect";
import errorToast from "helpers/errorToast";
import { ICurrencyOption } from "types/Common";

interface ApprovalFormValues {
  name: string;
  order: string;
  currency: string;
  selected_location: string[];
  amount: string;
  members: string[];
}

interface NewApprovalDialogProps {
  isOpen: boolean;
  close: () => void;
  id?: number;
  refetch: () => void;
  isLocation?: boolean;
  totalOrders: number;
  viewOnly?: boolean;
}

function NewApprovalDialog({ isOpen, close, id, isLocation, refetch, totalOrders, viewOnly }: NewApprovalDialogProps) {
  const defaultFormValues = {
    name: "",
    order: "",
    currency: "",
    ...(isLocation && { selected_location: [] }),
    amount: "",
    members: [],
  };

  const [isDeleting, setIsDeleting] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);

  const intl = useIntl();

  const methods = useForm<ApprovalFormValues>({
    defaultValues: defaultFormValues,
  });
  const { setValue, handleSubmit, reset, watch } = methods;

  const toggleMember = (id: string, isChecked: boolean) => {
    const members = watch("members") ?? [];
    const updatedMembers = isChecked ? [...members, id] : members.filter((memberId) => memberId !== id);
    setValue("members", updatedMembers);
  };

  const { data: createOptionsData = {} }: any = useGetApprovalLevelCreateOptions();
  const { available_payment_location = [], available_members = [] }: any = createOptionsData;

  const {
    data: approvalLevelDetailsData = {},
    isLoading: isLoadingApprovalLevelDetails,
    refetch: refetchApprovalLevelDetails,
  }: any = useGetApprovalLevelDetails(id!, {
    enabled: !!id,
  });

  useEffect(() => {
    if (id && approvalLevelDetailsData)
      reset({
        name: approvalLevelDetailsData.name,
        order: approvalLevelDetailsData.order,
        currency: approvalLevelDetailsData.currency,
        amount: approvalLevelDetailsData.amount,
        members: approvalLevelDetailsData.members?.map((m: any) => m.id),
        ...(isLocation && {
          selected_location: approvalLevelDetailsData.selected_location?.key,
        }),
      });
  }, [
    id,
    approvalLevelDetailsData.name,
    approvalLevelDetailsData.order,
    approvalLevelDetailsData.currency,
    approvalLevelDetailsData.amount,
    approvalLevelDetailsData.members,
    approvalLevelDetailsData.selected_location?.key,
    isLocation,
  ]);

  const { data: currencies = [] } = useGetCurrencies();
  const currencyOptions = currencies.map((currency) => ({
    key: currency.id,
    label: currency.code,
    name: currency.name,
    value: currency.code,
    code: currency.code,
  }));

  const handleCurrencyRenderOption = ({ option, text }: { option: ICurrencyOption; text: string }) => (
    <div className="p-2">
      <div className="font-weight-bold font-12">
        <Highlighter search={text}>{`(${option.code}) ${option.name}`}</Highlighter>
      </div>
    </div>
  );

  const handleSearchUser = (e: any) => {
    const { value } = e.target;
    setUserSearch(value);
  };

  const handleDeleteApprovalLevel = () => {
    if (!id || viewOnly) return;
    setIsDeleting(true);
    deleteApprovalLevel(id)
      .then(() => {
        toast("Approval level deleted", { type: "success" });
        refetch();
        handleCloseDialog();
      })
      .catch((err) => errorToast(err))
      .finally(() => setIsDeleting(false));
  };

  const { mutate, isLoading: isSubmitting } = useMutation({
    mutationFn: (payload) => (id ? updateApprovalLevel(id, payload) : createApprovalLevel(payload)),
    onSuccess: () => {
      toast(id ? "Approval level updated" : "Approval level saved", { type: "success" });
      refetch();
      if (id) refetchApprovalLevelDetails();
      handleCloseDialog();
    },
    onError: (err) => {
      errorToast(err);
    },
  });

  const onSubmit = (data: any) => {
    const { selected_location, ...resData } = data;
    mutate({
      ...resData,
      ...(isLocation && {
        selected_location,
      }),
    });
  };

  const handleCloseDialog = () => {
    close();
    reset(defaultFormValues);
    setShowConfirmDelete(false);
  };

  return (
    <Dialog
      isLoading={id && isLoadingApprovalLevelDetails}
      size="md"
      show={isOpen}
      title={intl.formatMessage({
        id: "settings.approval-level.approvalLevelDetails",
        defaultMessage: "Approval level details",
      })}
      onHide={handleCloseDialog}
    >
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="d-flex" style={{ marginBottom: "12px" }}>
            <Col xs={4} className="required">
              <FormattedMessage id="general.name" defaultMessage="Name" />
            </Col>
            <Col xs={8}>
              <Input
                name="name"
                placeholder={intl.formatMessage({
                  id: "general.name",
                  defaultMessage: "name",
                })}
                required
                className="input-width-lg rounded-input"
                disabled={viewOnly}
              />
            </Col>
          </div>

          {isLocation && (
            <div className="d-flex" style={{ marginBottom: "12px" }}>
              <Col xs={4} className="required">
                Payment location
              </Col>
              <Col xs={8}>
                <Dropdown
                  useHookForm
                  name="selected_location"
                  options={available_payment_location.map((location: any) => ({
                    value: location.key,
                    label: location.label,
                  }))}
                  required
                  placeholder={intl.formatMessage({
                    id: "general.pleaseSelect",
                    defaultMessage: "Please select",
                  })}
                  disabled={viewOnly}
                />
              </Col>
            </div>
          )}

          <div className="d-flex" style={{ marginBottom: "12px" }}>
            <Col xs={4} className="required">
              <FormattedMessage id="settings.approval-level.levelOrder" defaultMessage="Level order" />
            </Col>
            <Col xs={8}>
              <Dropdown
                useHookForm
                name="order"
                placeholder={intl.formatMessage({
                  id: "general.pleaseSelect",
                  defaultMessage: "Please select",
                })}
                options={Array.from(Array(id ? totalOrders : totalOrders + 1).keys()).map((i) => ({
                  value: i + 1,
                  label: i + 1,
                }))}
                required
                disabled={viewOnly}
              />
            </Col>
          </div>

          <div className="d-flex" style={{ marginBottom: "12px" }}>
            <Col xs={4} className="required">
              <FormattedMessage id="settings.approval-level.amount" defaultMessage="Amount" />
            </Col>

            <Col xs={8} className="d-flex justify-content-between">
              <div>
                <Input
                  name="amount"
                  required
                  placeholder={intl.formatMessage({
                    id: "settings.approval-level.amount",
                    defaultMessage: "amount",
                  })}
                  className="rounded-input input-width-sm"
                  disabled={viewOnly}
                />
              </div>

              <div>
                <SearchSelect
                  name="currency"
                  options={currencyOptions}
                  filterBy={["code", "name"]}
                  handleRenderOption={handleCurrencyRenderOption}
                  placeholder={intl.formatMessage({
                    id: "general.pleaseSelect",
                    defaultMessage: "Please select",
                  })}
                  disable={viewOnly}
                  required
                />
              </div>
            </Col>
          </div>

          <div className="d-flex" style={{ marginBottom: "12px" }}>
            <Col xs={4} className="required">
              <FormattedMessage id="settings.approval-level.roleUsers" defaultMessage="Role user" />
            </Col>
            <Col xs={8}>
              <div role="presentation">
                <ExpandableSearch containerClasses="mb-3" value={userSearch} alwaysExpanded onChange={handleSearchUser} />
                {available_members.length > 0 ? (
                  <div style={{ maxHeight: "220px", overflowY: "scroll" }}>
                    {available_members.map((member: any) => {
                      if (userSearch && !member.name.toLowerCase().includes(userSearch.toLowerCase())) return null;
                      const checked = watch("members")?.includes(member.id);
                      return (
                        <InputGroup className="checkbox bg-transparent mb-sm" key={member.id}>
                          <label className="d-flex align-items-center cursor-pointer mb-0 w-100">
                            <input
                              type="checkbox"
                              className="d-none"
                              checked={checked}
                              onChange={(e) => toggleMember(member.id, e.target.checked)}
                            />
                            <span className="border-dark border-solid border-1 mr-11 position-relative br-3 checkbox__box" />
                            <span>{member.name}</span>
                          </label>
                        </InputGroup>
                      );
                    })}
                  </div>
                ) : (
                  <div>
                    <span className="text-danger pl-3 pt-5">No Results!</span>
                  </div>
                )}
              </div>
            </Col>
          </div>

          {!showConfirmDelete && (
            <div className="d-flex justify-content-between">
              <Button variant="outline-primary" onClick={handleCloseDialog}>
                <FormattedMessage id="general.cancel" defaultMessage="cancel" />
              </Button>
              {!viewOnly && (
                <div className="d-flex">
                  {id && (
                    <Button variant="danger" onClick={() => setShowConfirmDelete(true)}>
                      <FormattedMessage id="tracking.delete" defaultMessage="Delete" />
                    </Button>
                  )}
                  <Button isLoading={isSubmitting} className="ml-3" type="submit">
                    {id
                      ? intl.formatMessage({
                          id: "settings.approval-level.editApprovalLevel",
                          defaultMessage: "Edit approval level",
                        })
                      : intl.formatMessage({
                          id: "settings.approval-level.createNewApprovalLevel",
                          defaultMessage: "Create new approval level",
                        })}
                  </Button>
                </div>
              )}
            </div>
          )}
          {showConfirmDelete && (
            <div style={{ width: "450px" }}>
              <Alert variant="warning" className="d-flex justify-content-between mb-3">
                <FormattedMessage
                  id="settings.approval-level.deleteApprovalLevelConfirm"
                  defaultMessage="Are you sure that you want to delete this approval level? Users with this approval level might not be able to book shipments or give approval. Please, make sure to review the approval levels for these users."
                />
              </Alert>
              <div className="d-flex justify-content-between">
                <Button onClick={() => setShowConfirmDelete(false)} variant="outline-primary">
                  <FormattedMessage id="general.cancel" defaultMessage="cancel" />
                </Button>
                <Button onClick={handleDeleteApprovalLevel} isLoading={isDeleting} variant="danger" className="ml-auto">
                  <FormattedMessage
                    id="settings.approval-level.deleteApprovalLevel"
                    defaultMessage="Delete approval level"
                  />
                </Button>
              </div>
            </div>
          )}
        </form>
      </FormProvider>
    </Dialog>
  );
}

NewApprovalDialog.defaultProps = {
  id: null,
  isLocation: false,
  viewOnly: true,
};

export default NewApprovalDialog;
